import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import {
  takeUntilDestroyed,
} from '@angular/core/rxjs-interop';
import {
  OrderStore,
} from '@des/store/order';
import {
  Store,
} from '@ngrx/store';
import {
  AnalyticsEvent,
  ConstructorAnalyticsEvent,
} from '@o3r/analytics';
import {
  ConfigurationBaseService,
  ConfigurationObserver,
  O3rConfig,
} from '@o3r/configuration';
import {
  DynamicConfigurable,
  O3rComponent,
} from '@o3r/core';
import {
  Localization,
} from '@o3r/localization';
import {
  DataLakeStore,
} from '@refx/analytics-core';
import {
  BACK_TO_MMB_PRES_CONFIG_ID,
  BACK_TO_MMB_PRES_DEFAULT_CONFIG,
  BackToMmbPresComponent,
} from '@refx/servicing-components';
import {
  DeeplinkReturnURLService,
} from '@refx/shared-services';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
} from 'rxjs';
import {
  DeepLinkApiResponse,
} from 'src/customization/contracts';
import {
  DeepLinkService,
} from 'src/customization/services';
import {
  customAnalyticsEvents,
  CustomBackToMmbPresAnalytics,
} from './custom-back-to-mmb-pres.analytics';
import {
  CUSTOM_BACK_TO_MMB_PRES_CONFIG_ID,
  CUSTOM_BACK_TO_MMB_PRES_DEFAULT_CONFIG,
  CustomMergedBackToMmbPresConfig,
} from './custom-back-to-mmb-pres.config';
import {
  CustomBackToMmbPresTranslation,
  customTranslations,
} from './custom-back-to-mmb-pres.translation';

@O3rComponent({
  componentType: 'Block'
})
@Component({
  selector: 'custom-back-to-mmb-pres',
  styleUrls: ['./custom-back-to-mmb-pres.style.scss'],
  templateUrl: './custom-back-to-mmb-pres.template.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class CustomBackToMmbPresComponent extends BackToMmbPresComponent implements OnChanges, OnInit, DynamicConfigurable<CustomMergedBackToMmbPresConfig> {
  /**
   * @inheritdoc
   */
  @Input()
  public flavor: undefined | 'refund';

  /** Deep link provided by the external API */
  public deepLink$: Observable<DeepLinkApiResponse | null>;

  @Input()
  public config: Partial<CustomMergedBackToMmbPresConfig> | undefined;

  @O3rConfig()
  protected dynamicConfig$: ConfigurationObserver<CustomMergedBackToMmbPresConfig>;

  public config$: Observable<CustomMergedBackToMmbPresConfig>;

  /**
   * Localization of the component
   */
  @Input()
  @Localization('./custom-back-to-mmb-pres.localization.json')
  public customTranslations: CustomBackToMmbPresTranslation;

  /**
   * @inheritdoc
   */
  public readonly customAnalyticsEvents: CustomBackToMmbPresAnalytics = customAnalyticsEvents;

  constructor(
    store: Store<OrderStore & DataLakeStore>,
    public deeplinkService: DeepLinkService,
    private readonly destroyRef: DestroyRef,
    protected deeplinkReturnURLService: DeeplinkReturnURLService,
    protected configurationService: ConfigurationBaseService
  ) {
    super(store, deeplinkReturnURLService, configurationService);

    const rootDynamicConfig$ = new ConfigurationObserver<any>(BACK_TO_MMB_PRES_CONFIG_ID, BACK_TO_MMB_PRES_DEFAULT_CONFIG, configurationService);
    this.dynamicConfig$ = new ConfigurationObserver<any>(CUSTOM_BACK_TO_MMB_PRES_CONFIG_ID, CUSTOM_BACK_TO_MMB_PRES_DEFAULT_CONFIG, configurationService);
    this.config$ = combineLatest([
      rootDynamicConfig$.asObservable(),
      this.dynamicConfig$.asObservable()
    ]).pipe(
      map(([root, dyn]) => {
        return {
          ...root,
          ...dyn
        };
      })
    );
    this.customTranslations = customTranslations;
  }

  public ngOnInit() {
    this.config$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((config: CustomMergedBackToMmbPresConfig) => {
      this.deepLink$ = this.deeplinkService.getDeepLink(this.flavor, config.airlineCode);
    });
  }

  /**
   * @override
   * @inheritdoc
   */
  public ngOnChanges(change: SimpleChanges) {
    super.ngOnChanges(change);
    // Run on every change of inputs
    if (change.config) {
      this.dynamicConfig$.next(this.config);
    }
  }

  /**
   * Compute the right analytics event to send depending on the usecase.
   * If we are in the refund usecase, it will return the refundLink event (same event than standard product)
   * Else, if we have a deeplink, it will return the exit-link-deeplink
   * Else it will return the exit-link fallback
   *
   * @returns the proper analytics event constructor.
   */
  public getAnalyticsEvent(): ConstructorAnalyticsEvent<AnalyticsEvent> {
    if (this.flavor === 'refund') {
      return this.customAnalyticsEvents.refundLink;
    }

    if (this.deepLink$ !== undefined) {
      this.deepLink$.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()).subscribe((deepLink) => {
        if (deepLink) {
          return this.customAnalyticsEvents.exitLinkWithDeepLink;
        }
      });
    }

    return this.customAnalyticsEvents.exitLinkWithFallbackLink;
  }
}
